/*
 * SMALL PASSIVE NOTES OVER THE CANVAS (2026-10-19): "135% - double-click the empty canvas to reset" while zoomed, "Showing /shop's flows - double-click it again to show everything" while one page is
 * focused. They are not controls: they ignore the pointer, so they can never be in the way of the thing under them, and each exists only while its state does. They stack in ONE container in the
 * top right corner of the canvas (so two of them never sit on top of each other), wherever the canvas is in its card.
 */
export interface CanvasNote {
  show: (text: string) => void;
  hide: () => void;
  /** takes the note away for good (and the container, once it holds no note) */
  remove: () => void;
  element: HTMLElement;
}

interface Stack {
  container: HTMLElement;
  host: HTMLElement;
  notes: number;
  observer: ResizeObserver | null;
  place: () => void;
}

const stacks = new WeakMap<SVGSVGElement, Stack>();

const stackFor = (svg: SVGSVGElement): Stack | null => {
  const existing = stacks.get(svg);
  if (existing) return existing;
  const host = (svg.closest(".analytics-card") as HTMLElement | null) ?? svg.parentElement;
  if (!host) return null;
  const container = document.createElement("div");
  container.className = "canvas-notes";
  host.appendChild(container);
  const place = () => {
    const s = svg.getBoundingClientRect();
    const h = host.getBoundingClientRect();
    container.style.right = `${Math.round(h.right - s.right + 10)}px`;
    container.style.top = `${Math.round(s.top - h.top + 10)}px`;
  };
  const observer = typeof ResizeObserver === "function" ? new ResizeObserver(place) : null;
  observer?.observe(svg);
  window.addEventListener("resize", place);
  const stack: Stack = { container, host, notes: 0, observer, place };
  stacks.set(svg, stack);
  place();

  return stack;
};

export const createNote = (svg: SVGSVGElement, className: string): CanvasNote => {
  const element = document.createElement("div");
  element.className = `canvas-note ${className}`;
  element.setAttribute("aria-hidden", "true");
  element.hidden = true;
  const stack = stackFor(svg);
  stack?.container.appendChild(element);
  if (stack) stack.notes++;
  let removed = false;

  return {
    element,
    show: (text) => {
      element.textContent = text;
      element.hidden = false;
      stack?.place();
    },
    hide: () => {
      element.hidden = true;
    },
    remove: () => {
      if (removed) return;
      removed = true;
      element.remove();
      if (!stack) return;
      stack.notes--;
      if (stack.notes <= 0) {
        stack.observer?.disconnect();
        window.removeEventListener("resize", stack.place);
        stack.container.remove();
        stacks.delete(svg);
      }
    },
  };
};
